import * as ss from 'superstruct';
import type { AdAccountPlatform } from '../../../ad-platforms/ad-account-platform';
import { OutputStreamDestinationConfig } from '../types';
import {
  ConversionOutputStreamDestination,
  ConversionOutputStreamDestinationList,
} from './destination-list';
import { OpenAiAdsOutputStreamDestination } from './openai-ads-output-stream';

type DestinationConfig = {
  /** Write-only fields, masked on read. */
  sensitiveFields: string[];
  /** The platform of the ad account its streams send to, if any. */
  adAccount?: AdAccountPlatform;
  /**
   * Checks the config whole when a stream is saved, throwing a `StructError`:
   * its consumer rejects an invalid config, which would otherwise block every
   * conversion after it's saved.
   */
  check?: (
    destination: OutputStreamDestinationConfig,
  ) => OutputStreamDestinationConfig;
};

/** Hardcoded config for each output stream destination. */
export const outputStreamDestinationConfig: Record<
  ConversionOutputStreamDestination,
  DestinationConfig
> = {
  email: { sensitiveFields: [] },
  webhook: { sensitiveFields: [] },
  'meta-ads': {
    sensitiveFields: ['accessToken'],
    adAccount: 'meta',
  },
  'google-ads-data-manager': {
    sensitiveFields: [],
    adAccount: 'google',
  },
  'microsoft-ads-conversion': {
    sensitiveFields: [],
    adAccount: 'bing',
  },
  'openai-ads': {
    sensitiveFields: ['apiKey'],
    adAccount: 'openai',
    check: (destination) =>
      ss.create(destination, OpenAiAdsOutputStreamDestination),
  },
};

const DestinationName = ss.enums(ConversionOutputStreamDestinationList);

const configFor = (destinationName: string): DestinationConfig | undefined =>
  ss.is(destinationName, DestinationName)
    ? outputStreamDestinationConfig[destinationName]
    : undefined;

export function getSensitiveFieldsForDestination(
  destinationName: string,
): string[] {
  return configFor(destinationName)?.sensitiveFields ?? [];
}

/** The platform of the ad account streams to `destinationName` send to. */
export const outputStreamAdAccountPlatform = (
  destinationName: string,
): AdAccountPlatform | undefined => configFor(destinationName)?.adAccount;

/**
 * `destination`, checked whole if its config has a `check`. Throws a
 * `StructError` with a user-facing message if it's invalid.
 *
 * The data provider validates writes against the generic
 * `OutputStreamDestinationConfig`, so this is how specific ones get checked.
 */
export const validateOutputStreamDestination = (
  destination: OutputStreamDestinationConfig,
): OutputStreamDestinationConfig => {
  const check = configFor(destination.name)?.check;

  return check ? check(destination) : destination;
};
